// Parses a merchant-pasted YouTube or Vimeo URL into a normalized embed URL for the
// "Before you go" retention page. Mirrors the proven logic in the checkout VSL template
// (lasso-checkout/.../vsl-preview.tsx) and ai-template-page.tsx.
//
// IMPORTANT: keep this file byte-identical to the lasso-web-app editor copy at
// lasso-web-app/src/pages/subscriptions/cancellation-offers/editor/videoEmbed.ts — the two
// apps build separately and must agree on what URLs they accept and how they embed them.

export type VideoProvider = 'youtube' | 'vimeo';

export interface EmbedOptions {
  autoplay: boolean;
  muted: boolean;
}

export interface ParsedVideo {
  provider: VideoProvider;
  id: string;
  // Builds the iframe src. `enablejsapi=1` (YouTube) / Vimeo's player API let us drive
  // autoplay-with-sound + an unmute fallback from the host page.
  embedUrl: (options: EmbedOptions) => string;
}

const VIMEO_PATTERN = /vimeo\.com\/(?:video\/)?(\d+)/;
// Covers youtube.com/watch?v=ID, youtu.be/ID, and youtube.com/embed/ID. A YouTube id is
// 11 chars of [A-Za-z0-9_-].
const YOUTUBE_PATTERN =
  /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

const buildVimeoEmbed =
  (id: string) =>
  (options: EmbedOptions): string => {
    const autoplay = options.autoplay ? 1 : 0;
    const muted = options.muted ? 1 : 0;
    return `https://player.vimeo.com/video/${id}?autoplay=${autoplay}&muted=${muted}&dnt=1&title=0&byline=0&portrait=0&controls=1`;
  };

const buildYouTubeEmbed =
  (id: string) =>
  (options: EmbedOptions): string => {
    const autoplay = options.autoplay ? 1 : 0;
    const muted = options.muted ? 1 : 0;
    return `https://www.youtube-nocookie.com/embed/${id}?autoplay=${autoplay}&mute=${muted}&playsinline=1&rel=0&enablejsapi=1`;
  };

export const parseVideoUrl = (url: string | null | undefined): ParsedVideo | null => {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const vimeoMatch = trimmed.match(VIMEO_PATTERN);
  if (vimeoMatch) {
    const vimeoId = vimeoMatch[1];
    return { provider: 'vimeo', id: vimeoId, embedUrl: buildVimeoEmbed(vimeoId) };
  }

  const youtubeMatch = trimmed.match(YOUTUBE_PATTERN);
  if (youtubeMatch) {
    const youtubeId = youtubeMatch[1];
    return { provider: 'youtube', id: youtubeId, embedUrl: buildYouTubeEmbed(youtubeId) };
  }

  return null;
};
